import { ChevronDown } from 'lucide-react';
import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type ProntuarioProfileVariant = 'dark' | 'light';

const ACCORDION_TAB_HEIGHT_CLASS = 'h-12 shrink-0';

const ACCORDION_THEME = {
	dark: {
		button: {
			active: 'bg-white/[0.06] text-slate-100',
			inactive:
				'bg-transparent text-slate-300 hover:bg-white/[0.04] hover:text-slate-100',
		},
		border: 'border-white/[0.08]',
		chevron: {
			base: 'shrink-0 text-slate-500 transition-transform duration-200',
			active: 'rotate-180 text-slate-300',
		},
	},
	light: {
		button: {
			active: 'bg-zinc-100 text-zinc-950',
			inactive:
				'bg-white text-zinc-700 hover:bg-zinc-50 hover:text-zinc-950',
		},
		border: 'border-zinc-200',
		chevron: {
			base: 'shrink-0 text-zinc-500 transition-transform duration-200',
			active: 'rotate-180 text-zinc-700',
		},
	},
} as const satisfies Record<
	ProntuarioProfileVariant,
	Record<string, string | Record<string, string>>
>;

type ProntuarioProfileAccordionBlockProps = {
	label: string;
	isActive: boolean;
	onClick: () => void;
	children: ReactNode;
	variant?: ProntuarioProfileVariant;
	contentMaxHeightClass?: string;
	contentScrollClass?: string;
	fillAvailable?: boolean;
};

const ProntuarioProfileAccordionBlock = ({
	label,
	isActive,
	onClick,
	children,
	variant = 'dark',
	contentMaxHeightClass,
	contentScrollClass,
	fillAvailable = false,
}: ProntuarioProfileAccordionBlockProps) => {
	const theme = ACCORDION_THEME[variant];

	const activeContentClass = fillAvailable
		? 'min-h-0 flex-1'
		: contentMaxHeightClass;

	return (
		<div
			className={cn(
				'flex flex-col',
				fillAvailable && isActive && 'min-h-0 flex-1',
				fillAvailable && !isActive && 'shrink-0',
			)}
		>
			<button
				type="button"
				onClick={onClick}
				aria-expanded={isActive}
				className={cn(
					'flex w-full items-center justify-between gap-3 border-b px-5 text-left transition-colors',
					ACCORDION_TAB_HEIGHT_CLASS,
					theme.border,
					isActive ? theme.button.active : theme.button.inactive,
				)}
			>
				<span className="text-sm font-semibold">{label}</span>
				<ChevronDown
					size={16}
					className={cn(
						theme.chevron.base,
						isActive && theme.chevron.active,
					)}
					aria-hidden
				/>
			</button>
			<div
				className={cn(
					contentScrollClass,
					'border-b',
					theme.border,
					isActive
						? activeContentClass
						: 'max-h-0 overflow-hidden border-b-0',
				)}
				aria-hidden={!isActive}
			>
				<div className="px-5 py-5">{children}</div>
			</div>
		</div>
	);
};

export {
	ProntuarioProfileAccordionBlock,
	type ProntuarioProfileVariant as ProntuarioProfileAccordionVariant,
};
